/**
 * UMA GRAVAÇÃO POR VEZ, no computador e no login (Valdair, 02/10).
 *
 * Caso real: a Dra gravava a consulta de uma paciente, trocou de aba sem parar e começou outra gravação na ficha de
 * outro paciente; as duas rodaram juntas por horas. A trava de 1 gravador por ABA (01/10) não via a outra aba.
 *
 * Aqui:
 *   - entre ABAS do mesmo navegador: BroadcastChannel (quem grava responde "sou eu: paciente X desde HH:MM") +
 *     Web Lock 'essence-gravacao' segurado enquanto grava (uma aba que não responder ainda aparece);
 *   - "Finalizar a outra": a aba que grava recebe o comando e PARA PELO FLUXO NORMAL (upload, plaud_recordings,
 *     sessão 'finalized'); quem pediu só começa quando ela avisa que salvou;
 *   - outro APARELHO (ou outro navegador) com o mesmo login: o banco sabe (migration 436). A tela pergunta antes de
 *     abrir o microfone e, se pedir para finalizar, marca a sessão; o outro aparelho lê a marca a cada pedaço e para.
 */
import { supabase } from '../supabaseClient';

export const NOME_DA_TRAVA = 'essence-gravacao';
const ESPERA_RESPOSTA_MS = 450;

export interface GravacaoViva {
  paciente: string;
  pacienteId: string;
  /** ISO de quando começou */
  desde: string;
  sessao: string | null;
  returnTo: string;
}

export interface OutraGravacao extends Partial<GravacaoViva> {
  onde: 'aba' | 'aparelho';
  /** a aba que grava (só 'aba') */
  aba?: string;
  /** a sessão já recebeu o pedido de parar (só 'aparelho') */
  paradaPedida?: boolean;
}

/** Como a gravação terminou depois de um "Parar". */
export type FimDaParada = 'salva' | 'descartada' | 'falhou' | 'sem_gravacao';

type Msg =
  | { t: 'quem'; de: string; pedido: string }
  | { t: 'sou'; para: string; pedido: string; aba: string; info: GravacaoViva }
  | { t: 'parar'; para: string; de: string; pedido: string }
  | { t: 'parou'; para: string; pedido: string; fim: FimDaParada }
  | { t: 'foco'; para: string };

export const ESTA_ABA: string = (() => { try { return crypto.randomUUID(); } catch { return `aba-${Date.now()}-${Math.random()}`; } })();

let canal: BroadcastChannel | null = null;
try { canal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(NOME_DA_TRAVA) : null; } catch { canal = null; }

// quem grava NESTA aba (registrado pelo gravador) e quem sabe parar (o gravador global)
let fonte: () => GravacaoViva | null = () => null;
let parador: (() => Promise<FimDaParada>) | null = null;
export function registrarFonteDaGravacao(f: () => GravacaoViva | null) { fonte = f; }
export function registrarParador(p: (() => Promise<FimDaParada>) | null) { parador = p; }
/** O "Parar" da faixa do topo: o mesmo fluxo que a outra aba pede. */
export function pararGravacaoNestaAba(): Promise<FimDaParada> { return parador ? parador() : Promise.resolve('sem_gravacao'); }

/** Aviso de "a outra aba pediu para você aparecer": pisca o título até a pessoa voltar. */
function chamarAtencao() {
  try { window.focus(); } catch { /* o navegador pode recusar */ }
  if (!document.hidden) return;
  const original = document.title;
  let n = 0;
  const iv = setInterval(() => {
    document.title = n++ % 2 ? original : '● Gravando — volte aqui';
    if (!document.hidden || n > 120) { clearInterval(iv); document.title = original; }
  }, 900);
}

const esperas = new Map<string, (m: Msg) => void>();
canal?.addEventListener('message', (ev: MessageEvent<Msg>) => {
  const m = ev.data;
  if (!m || typeof m !== 'object') return;
  if (m.t === 'quem') {
    const info = fonte();
    if (info) canal!.postMessage({ t: 'sou', para: m.de, pedido: m.pedido, aba: ESTA_ABA, info } as Msg);
    return;
  }
  if (m.t === 'parar' && m.para === ESTA_ABA) {
    const p = parador;
    (p ? p() : Promise.resolve<FimDaParada>('sem_gravacao'))
      .catch((): FimDaParada => 'falhou')
      .then(fim => canal!.postMessage({ t: 'parou', para: m.de, pedido: m.pedido, fim } as Msg));
    return;
  }
  if (m.t === 'foco' && m.para === ESTA_ABA) { chamarAtencao(); return; }
  if ((m.t === 'sou' || m.t === 'parou') && m.para === ESTA_ABA) esperas.get(m.pedido)?.(m);
});

const novoPedido = () => `${ESTA_ABA}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

// ── a trava do navegador, segurada enquanto esta aba grava ──────────────────────
let soltarTrava: (() => void) | null = null;
export function segurarTravaDaGravacao() {
  const locks = (navigator as any).locks;
  if (!locks || soltarTrava) return;
  locks.request(NOME_DA_TRAVA, () => new Promise<void>(res => { soltarTrava = () => { soltarTrava = null; res(); }; }))
    .catch(() => { soltarTrava = null; });
}
export function soltarTravaDaGravacao() { soltarTrava?.(); }
async function travaSeguradaPorOutraAba(): Promise<boolean> {
  const locks = (navigator as any).locks;
  if (!locks?.query) return false;
  try {
    const q = await locks.query();
    return (q.held || []).some((l: any) => l.name === NOME_DA_TRAVA) && !soltarTrava;
  } catch { return false; }
}

/** Outra gravação do mesmo computador (outra aba) ou do mesmo login (outro aparelho)? null = pode gravar. */
export async function procurarOutraGravacao(): Promise<OutraGravacao | null> {
  // 1) outra aba deste navegador responde com quem está gravando
  if (canal) {
    const pedido = novoPedido();
    const sou = await new Promise<Msg | null>(res => {
      const t = setTimeout(() => { esperas.delete(pedido); res(null); }, ESPERA_RESPOSTA_MS);
      esperas.set(pedido, m => { clearTimeout(t); esperas.delete(pedido); res(m); });
      canal!.postMessage({ t: 'quem', de: ESTA_ABA, pedido } as Msg);
    });
    if (sou && sou.t === 'sou') return { onde: 'aba', aba: sou.aba, ...sou.info };
  }
  // 2) a trava do navegador está com outra aba que não respondeu (ocupada): ainda é outra aba
  const travada = await travaSeguradaPorOutraAba();
  // 3) o banco: o mesmo login gravando em outro lugar, com pedaço nos últimos 2 min (migration 436)
  try {
    const { data, error } = await supabase.rpc('gravacao_em_andamento');
    if (!error && data && (data as any).sessao) {
      const d = data as any;
      return { onde: travada ? 'aba' : 'aparelho', paciente: d.paciente || '', pacienteId: d.paciente_id, desde: d.desde,
        sessao: d.sessao, paradaPedida: !!d.parada_pedida };
    }
  } catch { /* banco sem a 436 ou rede: segue só com o que o navegador sabe */ }
  if (travada) return { onde: 'aba' };
  return null;
}

/** "Finalizar a outra e começar esta" (outra aba): ela para pelo fluxo normal; resolve quando ela avisar. */
export function pararGravacaoDaOutraAba(aba: string | undefined, limiteMs = 180_000): Promise<FimDaParada | 'sem_resposta'> {
  if (!canal) return Promise.resolve('sem_resposta');
  const pedido = novoPedido();
  return new Promise(res => {
    const t = setTimeout(() => { esperas.delete(pedido); res('sem_resposta'); }, limiteMs);
    esperas.set(pedido, m => { if (m.t !== 'parou') return; clearTimeout(t); esperas.delete(pedido); res(m.fim); });
    // aba desconhecida (só a trava): o pedido vai a quem estiver gravando
    if (aba) canal!.postMessage({ t: 'parar', para: aba, de: ESTA_ABA, pedido } as Msg);
    else {
      const quem = novoPedido();
      esperas.set(quem, m => { if (m.t === 'sou') { esperas.delete(quem); canal!.postMessage({ t: 'parar', para: m.aba, de: ESTA_ABA, pedido } as Msg); } });
      canal!.postMessage({ t: 'quem', de: ESTA_ABA, pedido: quem } as Msg);
    }
  });
}

/** "Ir para a outra aba": ela tenta ganhar o foco e pisca o título. */
export function chamarAOutraAba(aba: string | undefined) {
  if (canal && aba) canal.postMessage({ t: 'foco', para: aba } as Msg);
}

/** Outro aparelho: marca a sessão para parar (o gravador de lá lê a marca a cada pedaço). */
export async function pedirParadaNoBanco(sessao: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('gravacao_pedir_parada', { p_sessao: sessao });
  return !error && data === true;
}

/** Espera o banco liberar (a outra sessão finalizou ou ficou 2 min sem pedaço). true = liberou. */
export async function esperarLiberarNoBanco(sessao: string, parar: () => boolean, limiteMs = 180_000): Promise<boolean> {
  const fim = Date.now() + limiteMs;
  while (Date.now() < fim) {
    await new Promise(r => setTimeout(r, 5_000));
    if (parar()) return false;
    try {
      const { data, error } = await supabase.rpc('gravacao_em_andamento');
      if (!error && (!data || (data as any).sessao !== sessao)) return true;
    } catch { /* tenta de novo */ }
  }
  return false;
}

/** A recusa do banco ao abrir a sessão (436) traz a outra gravação no DETAIL. */
export function lerRecusaDoBanco(error: { message?: string; details?: string | null } | null): OutraGravacao | null {
  if (!error || !/gravacao_em_andamento/.test(error.message || '')) return null;
  try {
    const d = JSON.parse(error.details || '{}');
    return { onde: 'aparelho', paciente: d.paciente || '', pacienteId: d.paciente_id, desde: d.desde, sessao: d.sessao, paradaPedida: !!d.parada_pedida };
  } catch { return { onde: 'aparelho' }; }
}
